// Download notification events and utilities
export interface DownloadNotification {
  type: 'success' | 'info' | 'warning' | 'error';
  title: string;
  message: string;
  fileName?: string;
  instructions?: string;
  duration?: number;
  downloadUrl?: string;
  blobPath?: string;
  expiresAt?: string;
  showRegenerateButton?: boolean;
}

export class DownloadNotificationService {
  private static readonly NOTIFICATION_EVENT = 'download-notification';

  static emit(notification: DownloadNotification): void {
    const event = new CustomEvent(this.NOTIFICATION_EVENT, {
      detail: notification
    });
    window.dispatchEvent(event);
  }

  static subscribe(callback: (notification: DownloadNotification) => void): () => void {
    const handler = (event: CustomEvent<DownloadNotification>) => {
      callback(event.detail);
    };
    
    window.addEventListener(this.NOTIFICATION_EVENT, handler as EventListener);
    
    // Return unsubscribe function
    return () => {
      window.removeEventListener(this.NOTIFICATION_EVENT, handler as EventListener);
    };
  }

  static success(title: string, message: string, fileName?: string): void {
    this.emit({
      type: 'success',
      title,
      message,
      fileName,
      duration: 5000
    });
  }

  static info(title: string, message: string, instructions?: string): void {
    this.emit({
      type: 'info',
      title,
      message,
      instructions,
      duration: 8000
    });
  }

  static warning(title: string, message: string, instructions?: string): void {
    this.emit({
      type: 'warning',
      title,
      message,
      instructions,
      duration: 10000
    });
  }

  static error(title: string, message: string): void {
    this.emit({
      type: 'error',
      title,
      message,
      duration: 6000
    });
  }

  static showDownloadReady(fileName: string, downloadUrl: string, blobPath: string, expiresAt: string): void {
    const expiryDate = new Date(expiresAt);
    const minutesUntilExpiry = Math.round((expiryDate.getTime() - Date.now()) / 60000);
    
    this.emit({
      type: 'success',
      title: 'Export Ready',
      message: `${fileName} is downloading. Link expires in ${minutesUntilExpiry} minutes.`,
      fileName,
      downloadUrl,
      blobPath,
      expiresAt,
      showRegenerateButton: true,
      duration: 2000 // Brief 2-second confirmation
    });
  }
}
